import { useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper, type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { ChevronLeft, ChevronRight, Minus, Plus, RefreshCw } from 'lucide-react';
import { FullScreenShell } from '../ui/FullScreenShell';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { comprobanteService } from '../../services/comprobanteService';
import type { ComprobanteResumen, HistorialComprobante, Respaldo } from '../../types/comprobante';
import { formatDate } from '../../utils/formatters';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { abrirPdfEnPestanaNueva } from './abrirComprobante';

interface ComprobanteVisorProps {
  readonly comprobante: ComprobanteResumen;
  readonly onClose: () => void;
  /** Muestra los comprobantes reemplazados u objetados de este respaldo. */
  readonly historial?: { readonly movimientoId: string; readonly respaldo: Respaldo };
  readonly onReemplazar?: () => void;
}

const BOTON_ZOOM = 'min-h-11 min-w-11 flex items-center justify-center rounded-xl border border-slate-200 text-slate-700 hover:border-slate-300';

interface ImagenConZoomProps {
  readonly url: string;
  readonly alt: string;
  readonly controlRef: (control: ReactZoomPanPinchRef | null) => void;
}

const ESCALA_DOBLE_TOQUE = 3;
const VENTANA_DOBLE_TOQUE_MS = 300;
const DESPLAZAMIENTO_MAX_TOQUE_PX = 10;

interface PuntoToque { readonly x: number; readonly y: number; readonly t: number }

/**
 * Una página con zoom propio: pellizco, doble toque y rueda amplían solo la imagen dentro del visor.
 * Sin zoom el gesto de un dedo queda libre para desplazar; con zoom, arrastra la imagen ampliada.
 * El doble toque alterna entre 1x y 3x sobre el punto tocado: el modo «toggle» de la librería solo
 * resta un paso fijo al alejar y la animación se corta con el levantar del dedo, así que se resuelve aquí.
 */
const ImagenConZoom: React.FC<ImagenConZoomProps> = ({ url, alt, controlRef }) => {
  const [ampliada, setAmpliada] = useState(false);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const controlLocal = useRef<ReactZoomPanPinchRef | null>(null);
  const inicioToque = useRef<PuntoToque | null>(null);
  const ultimoToque = useRef<PuntoToque | null>(null);
  const ultimoTactilEn = useRef(0);

  const alternarZoom = (clientX: number, clientY: number) => {
    const control = controlLocal.current;
    const caja = contenedorRef.current?.getBoundingClientRect();
    if (!control || !caja) return;
    if (ampliada) {
      control.resetTransform(150);
      return;
    }
    const factor = ESCALA_DOBLE_TOQUE - 1;
    control.setTransform(-(clientX - caja.left) * factor, -(clientY - caja.top) * factor, ESCALA_DOBLE_TOQUE, 150);
  };

  const alLevantarDedo = (e: React.TouchEvent) => {
    ultimoTactilEn.current = Date.now();
    const inicio = inicioToque.current;
    inicioToque.current = null;
    const dedo = e.changedTouches[0];
    // Solo cuenta un toque corto y quieto con un único dedo: ni un arrastre ni el final de un pellizco.
    if (!inicio || !dedo || e.touches.length > 0) { ultimoToque.current = null; return; }
    const ahora = Date.now();
    const quieto = Math.hypot(dedo.clientX - inicio.x, dedo.clientY - inicio.y) < DESPLAZAMIENTO_MAX_TOQUE_PX;
    if (!quieto || ahora - inicio.t > VENTANA_DOBLE_TOQUE_MS) { ultimoToque.current = null; return; }
    const previo = ultimoToque.current;
    if (previo && ahora - previo.t < VENTANA_DOBLE_TOQUE_MS
      && Math.hypot(dedo.clientX - previo.x, dedo.clientY - previo.y) < DESPLAZAMIENTO_MAX_TOQUE_PX * 4) {
      ultimoToque.current = null;
      alternarZoom(dedo.clientX, dedo.clientY);
      return;
    }
    ultimoToque.current = { x: dedo.clientX, y: dedo.clientY, t: ahora };
  };

  return (
    // El navegador emite además un «dblclick» sintético tras el doble toque: se ignora para no alternar dos veces.
    <div
      ref={contenedorRef}
      className="snap-center shrink-0 w-full h-full"
      onTouchStart={(e) => {
        const dedo = e.touches[0];
        inicioToque.current = e.touches.length === 1 && dedo ? { x: dedo.clientX, y: dedo.clientY, t: Date.now() } : null;
      }}
      onTouchEnd={alLevantarDedo}
      onDoubleClick={(e) => {
        if (Date.now() - ultimoTactilEn.current < 600) return;
        alternarZoom(e.clientX, e.clientY);
      }}
    >
      <TransformWrapper
        ref={(control) => { controlLocal.current = control; controlRef(control); }}
        minScale={1}
        maxScale={6}
        doubleClick={{ disabled: true }}
        wheel={{ step: 0.2 }}
        panning={{ disabled: !ampliada }}
        velocityAnimation={{ disabled: true }}
        onTransform={(_, { scale }) => { setAmpliada(scale > 1.001); }}
      >
        <TransformComponent
          wrapperClass="!w-full !h-full"
          contentClass="!w-full !h-full"
          wrapperStyle={{ touchAction: ampliada ? 'none' : 'pan-x pan-y' }}
        >
          <img src={url} alt={alt} draggable={false} className="w-full h-full object-contain select-none" />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
};

export const ComprobanteVisor: React.FC<ComprobanteVisorProps> = ({ comprobante, onClose, historial, onReemplazar }) => {
  const [actual, setActual] = useState<ComprobanteResumen>(comprobante);
  const [anteriores, setAnteriores] = useState<HistorialComprobante[]>([]);
  const [pagina, setPagina] = useState(0);
  const [intento, setIntento] = useState(0);
  const [cargado, setCargado] = useState<{ readonly id: string; readonly intento: number; readonly urls: string[] } | null>(null);
  const [fallo, setFallo] = useState<{ readonly id: string; readonly intento: number } | null>(null);
  const carruselRef = useRef<HTMLDivElement>(null);
  const zoomsRef = useRef<(ReactZoomPanPinchRef | null)[]>([]);

  // Lo cargado o fallado vale solo para este comprobante y este intento; con otro, se vuelve a "cargando".
  const urls = cargado?.id === actual.id && cargado.intento === intento ? cargado.urls : null;
  const error = fallo?.id === actual.id && fallo.intento === intento ? 'No pudimos abrir el comprobante' : null;

  useEffect(() => {
    let vigente = true;
    comprobanteService.firmarRutas(actual.rutas)
      .then((enlaces) => { if (vigente) setCargado({ id: actual.id, intento, urls: enlaces }); })
      .catch((e: unknown) => {
        if (!vigente) return;
        setFallo({ id: actual.id, intento });
        reportarErrorInesperado(e, 'No pudimos abrir el comprobante');
      });
    return () => { vigente = false; };
  }, [actual, intento]);

  useEffect(() => {
    if (!historial) return;
    comprobanteService.getHistorial(historial.movimientoId, historial.respaldo)
      .then(setAnteriores)
      .catch((e: unknown) => { reportarErrorInesperado(e, 'No pudimos cargar los comprobantes anteriores'); });
  }, [historial]);

  // Cada página sigue montada: al cambiar de página, las demás vuelven a 1x sin zoom ni desplazamiento.
  useEffect(() => {
    zoomsRef.current.forEach((control, i) => { if (i !== pagina) control?.resetTransform(0); });
  }, [pagina]);

  const irA = (indice: number) => {
    setPagina(indice);
    carruselRef.current?.children[indice]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const abrirAnterior = async (id: string) => {
    try {
      const anterior = await comprobanteService.getById(id);
      if (!anterior) return;
      if (anterior.tipo_archivo === 'pdf') { await abrirPdfEnPestanaNueva(anterior); return; }
      setPagina(0);
      setActual(anterior);
    } catch (e: unknown) {
      reportarErrorInesperado(e, 'No pudimos abrir el comprobante');
    }
  };

  const total = actual.rutas.length;

  return (
    <FullScreenShell isOpen onClose={onClose} title="Comprobante" variant="centered">
      {/* Alto fijo dentro del panel: la imagen cabe entera y los botones quedan siempre a la vista.
          Sin zoom del navegador aquí: el pellizco lo recibe solo la imagen. */}
      <div className="flex flex-col h-[calc(90vh-5.5rem)] supports-[height:100dvh]:h-[calc(90dvh-5.5rem)] touch-pan-x touch-pan-y">
        <div className="flex-1 min-h-0 p-4 pb-2">
          <FormErrorAlert error={error} action={{ label: 'Reintentar', onClick: () => { setIntento((n) => n + 1); } }} />
          {!urls && !error && (
            <p role="status" className="flex items-center gap-2 text-slate-500">
              <RefreshCw className="w-4 h-4 motion-safe:animate-spin" aria-hidden="true" /> Cargando…
            </p>
          )}
          {urls && (
            <div
              ref={carruselRef}
              className="flex h-full overflow-x-auto snap-x snap-mandatory gap-4"
              onScroll={(e) => {
                const el = e.currentTarget;
                setPagina(Math.round(el.scrollLeft / el.clientWidth));
              }}
            >
              {urls.map((url, i) => (
                <ImagenConZoom key={url} url={url} alt={`Página ${i + 1} de ${total}`}
                  controlRef={(control) => { zoomsRef.current[i] = control; }} />
              ))}
            </div>
          )}
        </div>
        {anteriores.length > 0 && (
          <section className="shrink-0 max-h-28 overflow-y-auto border-t border-slate-200 px-4 py-2">
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Comprobantes anteriores</h3>
            <ul className="space-y-1">
              {anteriores.map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-2 text-sm text-slate-600">
                  <span>
                    {h.motivo === 'objetado' ? `Objetado el ${formatDate(h.created_at)}: ${h.objecion_motivo ?? ''}` : `Reemplazado el ${formatDate(h.created_at)}`}
                  </span>
                  <button type="button" onClick={() => { void abrirAnterior(h.comprobante_id); }}
                    className="min-h-11 px-2 text-blue-600 hover:text-blue-800 font-medium">Ver</button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {/* Barra fija al pie del visor: no se desplaza con la imagen. */}
        <div className="shrink-0 flex flex-wrap items-center justify-center gap-2 border-t border-slate-200 bg-white px-4 py-2 rounded-b-2xl">
          {total > 1 && (
            <>
              <button type="button" onClick={() => { irA(Math.max(0, pagina - 1)); }} disabled={pagina === 0}
                aria-label="Página anterior" className={`${BOTON_ZOOM} disabled:opacity-40`}>
                <ChevronLeft className="w-6 h-6" aria-hidden="true" />
              </button>
              <span className="text-sm text-slate-600">Página {pagina + 1} de {total}</span>
              <button type="button" onClick={() => { irA(Math.min(total - 1, pagina + 1)); }} disabled={pagina === total - 1}
                aria-label="Página siguiente" className={`${BOTON_ZOOM} disabled:opacity-40`}>
                <ChevronRight className="w-6 h-6" aria-hidden="true" />
              </button>
            </>
          )}
          {urls && (
            <>
              <button type="button" onClick={() => { zoomsRef.current[pagina]?.zoomOut(); }}
                aria-label="Alejar" className={BOTON_ZOOM}>
                <Minus className="w-5 h-5" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => { zoomsRef.current[pagina]?.zoomIn(); }}
                aria-label="Acercar" className={BOTON_ZOOM}>
                <Plus className="w-5 h-5" aria-hidden="true" />
              </button>
            </>
          )}
          {onReemplazar && (
            <button type="button" onClick={onReemplazar}
              className="min-h-11 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:border-slate-300">
              Reemplazar comprobante
            </button>
          )}
        </div>
      </div>
    </FullScreenShell>
  );
};
